import type { CSSProperties, ReactNode } from "react";

export interface FieldTitleFontSizeProps {
  titleFontSize?: number;
  children: ReactNode;
}

/**
 * Applies a field's author-configured `titleFontSize` to whatever renders that field's title.
 *
 * A field's title reaches the DOM through ~38 different field components, as a `<Label>` or a
 * heading, so the size is applied by descendant selector from here rather than threaded as a prop
 * through every one of them. `Label` sets its own `text-sm`, which an inherited `font-size` would
 * lose to, hence the explicit `[data-slot=label]` / heading rules.
 *
 * The `:not([for])` is what keeps this to the *title*. A field's title label is never bound to an
 * input, while every label that captions a control inside the field — each checkbox and radio
 * option, the Address sub-inputs — carries `htmlFor`. Without the exclusion those option captions
 * scale up with the title, which is not what `titleFontSize` means.
 *
 * Renders nothing of its own when unset, so a field with no configured size keeps the design
 * system's default type scale rather than legacy's flat 20px.
 */
export const FieldTitleFontSizeWrapper = ({ titleFontSize, children }: FieldTitleFontSizeProps) => {
  if (titleFontSize === undefined) return <>{children}</>;

  const style: CSSProperties & Record<"--field-title-size", string> = {
    "--field-title-size": `${titleFontSize}px`,
  };

  return (
    <div
      className="[&_[data-slot=label]:not([for])]:text-[length:var(--field-title-size)] [&_h3]:text-[length:var(--field-title-size)] [&_h4]:text-[length:var(--field-title-size)]"
      style={style}
    >
      {children}
    </div>
  );
};
